import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Checkbox,
  Input,
  Popover,
  Progress,
  Spin,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  FileTextOutlined,
  ReloadOutlined,
  SendOutlined,
  LeftOutlined,
  RightOutlined,
  EyeOutlined,
  EditOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { getEndpoint } from "../utils/endpoints";
import "./PECockpit.css";

// --- API types (mirror the backend contract) ---
type IssueClass =
  | "terminology"
  | "omission"
  | "tag"
  | "number"
  | "wrapper"
  | "untranslated"
  | "accuracy"
  | "fluency"
  | "style"
  | "manual";

interface Occurrence {
  row_number: number;
  paragraph: number;
  target: string;
  ok: boolean;
}

interface Issue {
  id: string;
  class: IssueClass;
  severity: "major" | "minor" | "info";
  paragraph: number | null;
  row_number: number | null;
  source: string;
  target: string;
  suggested: string | null;
  rationale: string;
  confidence: number;
  term?: string;
  canonical?: string;
  renderings?: string[];
  group_key?: string;
  occurrences?: Occurrence[];
}

interface Score {
  mqm: number;
  by_dim: Record<string, number>;
}

interface DocRow {
  row_number: number;
  paragraph?: number;
  source: string;
  target: string;
}

// --- class presentation (color-coded, no emoji) ---
const CLASS_META: Record<IssueClass, { color: string; label: string; chip: string }> = {
  terminology: { color: "#2f54eb", label: "Termine", chip: "Termini" },
  omission: { color: "#d4380d", label: "Omissione", chip: "Omiss" },
  tag: { color: "#722ed1", label: "Tag", chip: "Tag" },
  number: { color: "#08979c", label: "Numero", chip: "Numeri" },
  wrapper: { color: "#d4380d", label: "Wrapper", chip: "Tag" },
  untranslated: { color: "#d4380d", label: "Non tradotto", chip: "Termini" },
  accuracy: { color: "#cf1322", label: "Accuratezza", chip: "Accuratezza" },
  fluency: { color: "#d46b08", label: "Fluency", chip: "Fluency" },
  style: { color: "#d46b08", label: "Stile", chip: "Fluency" },
  manual: { color: "#2f54eb", label: "Manuale", chip: "Termini" },
};

const FILTER_CHIPS: { key: string; label: string; classes: IssueClass[] }[] = [
  { key: "all", label: "Tutti", classes: [] },
  { key: "accuracy", label: "Accuratezza", classes: ["accuracy", "omission"] },
  { key: "terminology", label: "Termini", classes: ["terminology", "untranslated", "manual"] },
  { key: "fluency", label: "Fluency", classes: ["fluency", "style"] },
  { key: "tag", label: "Tag", classes: ["tag", "wrapper"] },
  { key: "number", label: "Numeri", classes: ["number"] },
];


const stripHtml = (s: string) => (s || "").replace(/<[^>]*>/g, "");

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
// Read-only highlight of the canonical term (and simple inflections) inside a target.
const highlightTerm = (text: string, canonical?: string) => {
  const plain = escapeHtml(stripHtml(text));
  if (!canonical || canonical.length < 2) return plain;
  const stem = canonical.length > 4 ? canonical.slice(0, -1) : canonical;
  try {
    const re = new RegExp(`(${escapeRe(stem)}[\\p{L}]*)`, "giu");
    return plain.replace(re, '<mark class="pe-hl">$1</mark>');
  } catch {
    return plain;
  }
};

// small AI "sparkle" mark
const Sparkle = () => (
  <svg className="pe-sparkle" viewBox="0 0 24 24" width="18" height="18" aria-hidden>
    <path
      fill="currentColor"
      d="M12 2l1.9 5.1L19 9l-5.1 1.9L12 16l-1.9-5.1L5 9l5.1-1.9L12 2z"
    />
    <path fill="currentColor" opacity="0.7" d="M19 14l.9 2.4L22 17l-2.1.8L19 20l-.9-2.2L16 17l2.1-.6L19 14z" />
  </svg>
);

const PECockpit: React.FC = () => {
  const { document_uuid } = useParams();
  const BACKEND = getEndpoint("BACKEND");

  // scan / issues state
  const [status, setStatus] = useState<"loading" | "ready" | "error" | "none">("loading");
  const [issues, setIssues] = useState<Issue[]>([]);
  const [score, setScore] = useState<Score | null>(null);
  const [segmentQe, setSegmentQe] = useState<Record<string, number>>({});
  // per-occurrence review editor state (terminology): edited text + applied rows
  const [occDraft, setOccDraft] = useState<Record<number, string>>({});
  const [occApplied, setOccApplied] = useState<Set<number>>(new Set());
  const [occEditing, setOccEditing] = useState<Set<number>>(new Set());
  const [done, setDone] = useState<Set<string>>(new Set()); // accepted/rejected ids

  // doc rows for preview
  const [rows, setRows] = useState<DocRow[]>([]);
  const [docName, setDocName] = useState<string>("");

  // view state
  const [view, setView] = useState<"lista" | "focus">("lista");
  const [showPreview, setShowPreview] = useState(true);
  const [inboxOpen, setInboxOpen] = useState(true);
  const [inboxW, setInboxW] = useState(300);
  const [previewW, setPreviewW] = useState(360);
  const [showHowto, setShowHowto] = useState(true);
  const listaRef = useRef<HTMLDivElement>(null);
  const [filter, setFilter] = useState<string>("all");
  const [expandedCases, setExpandedCases] = useState<Set<string>>(new Set());
  const [showResolved, setShowResolved] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<string>(""); // editable suggestion for selected

  // add-term form
  const [addOpen, setAddOpen] = useState(false);
  const [addSource, setAddSource] = useState("");
  const [addTarget, setAddTarget] = useState("");
  const [addAll, setAddAll] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  const pollRef = useRef<number | null>(null);
  const previewRowRef = useRef<HTMLDivElement | null>(null);
  const previewScrollRef = useRef<HTMLDivElement | null>(null);
  const savedScrollRef = useRef(0);

  // --- fetch doc rows for the preview pane (same endpoint as DocumentDetails2) ---
  useEffect(() => {
    if (!document_uuid) return;
    fetch(`${BACKEND}/getDocumentsDetails/v2/${document_uuid}/`, {
      method: "GET",
      mode: "cors",
      headers: { Accept: "application/json" },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        setRows(Array.isArray(data?.document_rows) ? data.document_rows : []);
        setDocName(data?.document_name || "");
      })
      .catch((e) => console.error("doc rows fetch failed:", e));
  }, [BACKEND, document_uuid]);

  // --- scan + poll (mirrors handleSuggest polling) ---
  const poll = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND}/pe/issues/${document_uuid}`);
      const st = r.data?.status;
      if (st === "ready" || st === "error" || st === "none") {
        setIssues(Array.isArray(r.data?.issues) ? r.data.issues : []);
        setScore(r.data?.score || null);
        setSegmentQe(r.data?.segment_qe || {});
        setStatus(st);
      } else {
        pollRef.current = window.setTimeout(poll, 4000);
      }
    } catch {
      pollRef.current = window.setTimeout(poll, 4000); // transient -> keep polling
    }
  }, [BACKEND, document_uuid]);

  // Start a fresh scan (also used by the "Ri-scansiona" button).
  const startScan = useCallback(() => {
    setStatus("loading");
    if (pollRef.current) window.clearTimeout(pollRef.current);
    axios
      .post(`${BACKEND}/pe/issues/scan/${document_uuid}`)
      .catch((e) => console.error("scan start failed:", e))
      .finally(() => {
        pollRef.current = window.setTimeout(poll, 4000);
      });
  }, [BACKEND, document_uuid, poll]);

  // On mount: reuse a cached scan if one is already ready (a full scan takes minutes);
  // only launch a new scan when there is nothing ready yet.
  useEffect(() => {
    if (!document_uuid) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND}/pe/issues/${document_uuid}`);
        if (cancelled) return;
        if (r.data?.status === "ready") {
          setIssues(Array.isArray(r.data?.issues) ? r.data.issues : []);
          setScore(r.data?.score || null);
          setSegmentQe(r.data?.segment_qe || {});
          setStatus("ready");
          return;
        }
      } catch {
        /* fall through to a fresh scan */
      }
      if (!cancelled) startScan();
    })();
    return () => {
      cancelled = true;
      if (pollRef.current) window.clearTimeout(pollRef.current);
    };
  }, [BACKEND, document_uuid, startScan]);

  // --- derived: visible (open) issues, filtered ---
  const activeChip = FILTER_CHIPS.find((c) => c.key === filter) || FILTER_CHIPS[0];
  const visibleIssues = useMemo(
    () =>
      issues.filter(
        (i) =>
          !done.has(i.id) &&
          (activeChip.classes.length === 0 || activeChip.classes.includes(i.class))
      ),
    [issues, done, activeChip]
  );

  const selected = useMemo(
    () => issues.find((i) => i.id === selectedId) || null,
    [issues, selectedId]
  );

  // keep a valid selection as issues resolve / filter changes
  useEffect(() => {
    if (visibleIssues.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !visibleIssues.some((i) => i.id === selectedId)) {
      setSelectedId(visibleIssues[0].id);
    }
  }, [visibleIssues, selectedId]);

  // seed the editable suggestion when selection changes
  useEffect(() => {
    setDraft(selected?.suggested ?? selected?.target ?? "");
  }, [selected]);

  // scroll preview to the selected row only when the selection/view changes (not on reopen).
  useEffect(() => {
    previewRowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, view]);

  // restore the previous scroll position when the preview is reopened
  useEffect(() => {
    if (showPreview && previewScrollRef.current) {
      previewScrollRef.current.scrollTop = savedScrollRef.current;
    }
  }, [showPreview]);

  const collapsePreview = () => {
    if (previewScrollRef.current) savedScrollRef.current = previewScrollRef.current.scrollTop;
    setShowPreview(false);
  };

  // Drag to resize a side panel; each side is clamped to 50% of the cockpit width.
  const startResize = (side: "inbox" | "preview") => (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = side === "inbox" ? inboxW : previewW;
    const containerW = listaRef.current?.clientWidth || window.innerWidth;
    const maxW = Math.round(containerW * 0.5);
    const onMove = (ev: MouseEvent) => {
      let delta = ev.clientX - startX;
      if (side === "preview") delta = -delta;
      const w = Math.max(220, Math.min(maxW, startW + delta));
      side === "inbox" ? setInboxW(w) : setPreviewW(w);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const total = issues.length;
  const resolvedCount = done.size;
  const estMin = Math.max(1, Math.ceil((total - resolvedCount) * 0.5)); // ponytail: 30s/issue heuristic

  // --- apply a fix to a set of row updates, then mark issues done + patch preview ---
  const applyUpdates = useCallback(
    async (updates: { row_number: number; after: string }[], resolveIds: string[]) => {
      if (updates.length === 0) {
        setDone((d) => new Set(Array.from(d).concat(resolveIds)));
        return;
      }
      try {
        await axios.post(`${BACKEND}/pe/issues/apply/${document_uuid}`, { updates });
        setRows((rs) =>
          rs.map((row) => {
            const u = updates.find((x) => x.row_number === row.row_number);
            return u ? { ...row, target: u.after } : row;
          })
        );
        setDone((d) => new Set(Array.from(d).concat(resolveIds)));
      } catch (e) {
        console.error("apply failed:", e);
        message.error("Errore nell'applicare la correzione.");
      }
    },
    [BACKEND, document_uuid]
  );

  const acceptIssue = useCallback(
    (issue: Issue, override?: string) => {
      const after = override ?? issue.suggested ?? "";
      const updates: { row_number: number; after: string }[] = [];
      if (issue.occurrences && issue.occurrences.length > 0) {
        // terminology group: each occurrence carries its OWN corrected target (rewritten
        // with agreement) - apply that per row, NOT the primary sentence.
        issue.occurrences.forEach((o) => {
          if (!o.ok && o.target) updates.push({ row_number: o.row_number, after: o.target });
        });
      } else if (issue.row_number != null) {
        updates.push({ row_number: issue.row_number, after });
      }
      applyUpdates(updates, [issue.id]);
    },
    [applyUpdates]
  );

  const rejectIssue = useCallback((issue: Issue) => {
    setDone((d) => new Set(Array.from(d).concat(issue.id)));
  }, []);

  // Confirm a SINGLE occurrence (review-editor row).
  const applyOne = useCallback(
    async (rn: number, text: string) => {
      try {
        await axios.post(`${BACKEND}/pe/issues/apply/${document_uuid}`, {
          updates: [{ row_number: rn, after: text }],
        });
        setRows((rs) => rs.map((row) => (row.row_number === rn ? { ...row, target: text } : row)));
        setOccApplied((s) => new Set(s).add(rn));
      } catch (e) {
        console.error("apply one failed:", e);
        message.error("Errore nell'applicare la correzione.");
      }
    },
    [BACKEND, document_uuid]
  );

  const goNext = useCallback(() => {
    const idx = visibleIssues.findIndex((i) => i.id === selectedId);
    const next = visibleIssues[idx + 1] || visibleIssues[0];
    if (next) setSelectedId(next.id);
  }, [visibleIssues, selectedId]);

  // --- batch: fix all high-confidence issues with a suggestion ---
  const batchHighConfidence = useCallback(() => {
    const hi = issues.filter(
      (i) => !done.has(i.id) && i.confidence >= 0.9 && i.suggested != null
    );
    if (hi.length === 0) {
      message.info("Nessuna issue high-confidence da applicare.");
      return;
    }
    const updates: { row_number: number; after: string }[] = [];
    const ids: string[] = [];
    hi.forEach((issue) => {
      ids.push(issue.id);
      if (issue.occurrences && issue.occurrences.length > 0) {
        issue.occurrences.forEach(
          (o) => !o.ok && o.target && updates.push({ row_number: o.row_number, after: o.target })
        );
      } else if (issue.row_number != null) {
        updates.push({ row_number: issue.row_number, after: issue.suggested! });
      }
    });
    applyUpdates(updates, ids);
    message.success(`Applicate ${hi.length} correzioni high-confidence.`);
  }, [issues, done, applyUpdates]);

  // --- add-term: reuse termSwap/preview -> synthesize a manual issue ---
  const submitAddTerm = useCallback(async () => {
    if (!addSource.trim() || !addTarget.trim()) return;
    setAddLoading(true);
    try {
      const resp = await axios.post(`${BACKEND}/pe/termSwap/preview/${document_uuid}`, {
        source_term: addSource.trim(),
        new_target: addTarget.trim(),
        include_unchanged: addAll,
      });
      const prevRows = resp.data?.rows || [];
      const changed = prevRows.filter((r: any) => r.changed);
      if (changed.length === 0) {
        message.info("Nessuna occorrenza da correggere per questo termine.");
        setAddLoading(false);
        return;
      }
      const occ: Occurrence[] = prevRows.map((r: any) => ({
        row_number: r.row_number,
        paragraph: r.paragraph ?? 0,
        target: r.after,
        ok: !r.changed,
      }));
      const manual: Issue = {
        id: `manual-${addSource.trim()}-${Date.now()}`,
        class: "manual",
        severity: "minor",
        paragraph: changed[0].paragraph ?? null,
        row_number: changed[0].row_number ?? null,
        source: addSource.trim(),
        target: changed[0].before,
        suggested: changed[0].after,
        rationale: `Sostituzione manuale "${addSource.trim()}" "${addTarget.trim()}"`,
        confidence: 1,
        occurrences: occ,
      };
      setIssues((is) => [manual, ...is]);
      setSelectedId(manual.id);
      setAddOpen(false);
      setAddSource("");
      setAddTarget("");
    } catch (e) {
      console.error("add-term preview failed:", e);
      message.error("Errore nell'anteprima del termine.");
    } finally {
      setAddLoading(false);
    }
  }, [BACKEND, document_uuid, addSource, addTarget, addAll]);

  // --- keyboard shortcuts (focus view) ---
  useEffect(() => {
    if (view !== "focus" || !selected) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return; // don't hijack typing
      if (e.key === "a") acceptIssue(selected, draft);
      else if (e.key === "e") (document.getElementById("pe-focus-edit") as HTMLElement)?.focus();
      else if (e.key === "x") rejectIssue(selected);
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view, selected, draft, acceptIssue, rejectIssue, goNext]);

  // --- render helpers ---
  const rowsByParagraph = useMemo(() => {
    const map = new Map<number, DocRow[]>();
    rows.forEach((r) => {
      const p = r.paragraph ?? 0;
      if (!map.has(p)) map.set(p, []);
      map.get(p)!.push(r);
    });
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [rows]);

  // Distinctive one-line label for an inbox item.
  // MQM issues -> the specific error note (so several issues on the same long row are
  // told apart); otherwise the source snippet.
  const issueNote = (i: Issue) => (i.rationale || "").replace(/^MQM\/[a-z]+:\s*/i, "").trim();

  // The quoted source phrase an MQM note is about (used to cluster recurring errors).
  const extractQuoted = (s: string) => {
    const m = s.match(/[«"'“‘]([^«»"'”’]{2,80})[»"'”’]/);
    return m ? m[1].trim() : null;
  };
  // "case" = what groups several rows under one heading.
  const caseKey = (i: Issue): string => {
    if (i.class === "terminology") return `t:${(i.term || i.id).toLowerCase()}`;
    if (i.group_key) return `g:${i.group_key}`; // backend-provided stable grouping
    const q = extractQuoted(i.rationale || "");
    return q ? `q:${i.class}:${q.toLowerCase()}` : `x:${i.id}`;
  };
  const caseLabel = (i: Issue): string => {
    if (i.class === "terminology") return `«${i.term || "?"}» → «${i.canonical || "?"}»`;
    const q = extractQuoted(i.rationale || "");
    return q ? `«${q}»` : issueNote(i).slice(0, 70) || CLASS_META[i.class].label;
  };

  // Group the (filtered) issues into cases: [{ key, label, class, issues[] }]
  const caseGroups = useMemo(() => {
    const m = new Map<string, { key: string; label: string; cls: IssueClass; issues: Issue[] }>();
    for (const i of visibleIssues) {
      const key = caseKey(i);
      if (!m.has(key)) m.set(key, { key, label: caseLabel(i), cls: i.class, issues: [] });
      m.get(key)!.issues.push(i);
    }
    return Array.from(m.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleIssues]);

  // resolved (accepted/rejected) issues matching the active filter — kept, not deleted
  const resolvedIssues = useMemo(
    () =>
      issues.filter(
        (i) =>
          done.has(i.id) &&
          (activeChip.classes.length === 0 || activeChip.classes.includes(i.class))
      ),
    [issues, done, activeChip]
  );

  // ---- HEADER ----
  const pct = total ? Math.round((resolvedCount / total) * 100) : 0;
  const header = (
    <div className="pe-header">
      <div className="pe-header-left">
        <FileTextOutlined className="pe-doc-icon" />
        <strong className="pe-doc-name">{docName || "Documento"}</strong>
      </div>

      <div className="pe-header-right">
        <Popover
          title="Scorecard MQM"
          content={
            score ? (
              <div className="pe-scorecard">
                {Object.entries(score.by_dim || {}).map(([dim, val]) => (
                  <div className="pe-scorecard-row" key={dim}>
                    <span className="pe-scorecard-dim">{dim}</span>
                    <Progress
                      percent={val}
                      size="small"
                      strokeColor={val >= 90 ? "#12b76a" : val >= 70 ? "#f79009" : "#f04438"}
                      style={{ width: 160 }}
                    />
                  </div>
                ))}
                <div className="pe-scorecard-note">
                  Stima reference-free (MQM), da calibrare su campioni umani.
                </div>
              </div>
            ) : (
              "Nessuno score ancora."
            )
          }
        >
          <div className="pe-chip pe-mqm">
            <span className="pe-mqm-label">MQM</span>
            <span className="pe-mqm-value">{score ? score.mqm : "—"}</span>
          </div>
        </Popover>

        <Tooltip title={`${resolvedCount} di ${total} risolti · stima ~${estMin} min`}>
          <div className="pe-chip pe-progress">
            <Progress percent={pct} size="small" showInfo={false} style={{ width: 84 }} />
            <span className="pe-progress-text">
              {resolvedCount}/{total}
            </span>
          </div>
        </Tooltip>

        <span className="pe-toolbar-sep" />

        <Tooltip title="Ri-analizza il documento da capo (alcuni minuti)">
          <Button icon={<ReloadOutlined />} onClick={startScan} disabled={status === "loading"}>
            Ri-scansiona
          </Button>
        </Tooltip>
        <Tooltip title="In arrivo">
          <Button type="primary" icon={<SendOutlined />} disabled>
            Consegna
          </Button>
        </Tooltip>
      </div>
    </div>
  );

  // ---- INBOX (shared by lista) ----
  const openCount = issues.filter((i) => !done.has(i.id)).length;
  const inbox = (
    <div className="pe-inbox">
      <div className="pe-panel-header">
        {view === "lista" && (
          <Tooltip title="Nascondi pannello">
            <button
              className="pe-icon-btn"
              onClick={() => setInboxOpen(false)}
              aria-label="Nascondi problemi"
            >
              <LeftOutlined />
            </button>
          </Tooltip>
        )}
        <Sparkle />
        <span className="pe-panel-title">Suggerimenti</span>
        <span className="pe-panel-count">{openCount}/{total}</span>
      </div>

      <div className="pe-chips">
        {FILTER_CHIPS.map((c) => {
          const n =
            c.classes.length === 0
              ? openCount
              : issues.filter((i) => !done.has(i.id) && c.classes.includes(i.class)).length;
          return (
            <Tag.CheckableTag
              key={c.key}
              checked={filter === c.key}
              onChange={() => setFilter(c.key)}
            >
              {c.label} {n > 0 ? `(${n})` : ""}
            </Tag.CheckableTag>
          );
        })}
      </div>

      <div className="pe-issue-list">
        <div className="pe-section">Aperti · {openCount}</div>
        {caseGroups.length === 0 && <div className="pe-empty">Nessun problema aperto.</div>}
        {caseGroups.map((g) => {
          const meta = CLASS_META[g.cls];
          const multi = g.issues.length > 1;
          const expanded = expandedCases.has(g.key);
          const groupSelected = g.issues.some((i) => i.id === selectedId);
          const worst = g.issues.some((i) => i.severity === "major") ? "major" : "minor";
          return (
            <div key={g.key} className="pe-case">
              <div
                className={`pe-case-head sev-${worst} ${groupSelected ? "selected" : ""}`}
                onClick={() => {
                  if (multi) {
                    setExpandedCases((s) => {
                      const n = new Set(s);
                      n.has(g.key) ? n.delete(g.key) : n.add(g.key);
                      return n;
                    });
                    if (!expanded) setSelectedId(g.issues[0].id);
                  } else {
                    setSelectedId(g.issues[0].id);
                  }
                }}
              >
                <span className="pe-caret">{multi ? (expanded ? "▾" : "▸") : ""}</span>
                <span className="pe-case-body">
                  <span className="pe-case-label">{g.label}</span>
                  <span className="pe-case-sub">
                    {meta.label}
                    {multi ? ` · ${g.issues.length} righe` : ` · riga ${g.issues[0].row_number}`}
                  </span>
                </span>
                <span className={`badge badge--${worst}`}>{worst}</span>
              </div>
              {multi && expanded && (
                <div className="pe-case-rows">
                  {g.issues.map((i) => (
                    <div
                      key={i.id}
                      className={`pe-case-row ${i.id === selectedId ? "selected" : ""}`}
                      onClick={() => setSelectedId(i.id)}
                    >
                      <span className="pe-case-row-line">riga {i.row_number}</span>
                      <span className="pe-case-row-note">{issueNote(i).slice(0, 46)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* manual add — right below the problems */}
        <div className="pe-manual">
          <Button block type="dashed" onClick={() => setAddOpen((o) => !o)}>
            ＋ Aggiungi un termine da correggere
          </Button>
          {addOpen && (
            <div className="pe-addterm">
              <Input
                size="small"
                placeholder="termine (es. screw)"
                value={addSource}
                onChange={(e) => setAddSource(e.target.value)}
              />
              <Input
                size="small"
                placeholder="nuovo traducente (es. vite)"
                value={addTarget}
                onChange={(e) => setAddTarget(e.target.value)}
              />
              <Checkbox checked={addAll} onChange={(e) => setAddAll(e.target.checked)}>
                mostra tutte le occorrenze
              </Checkbox>
              <Button
                type="primary"
                size="small"
                block
                loading={addLoading}
                disabled={!addSource.trim() || !addTarget.trim()}
                onClick={submitAddTerm}
              >
                Anteprima
              </Button>
            </div>
          )}
        </div>

        {/* resolved — kept, not deleted */}
        {resolvedIssues.length > 0 && (
          <>
            <div
              className="pe-section pe-section-toggle"
              onClick={() => setShowResolved((s) => !s)}
            >
              <span className="pe-caret">{showResolved ? "▾" : "▸"}</span>
              Risolti · {resolvedIssues.length}
            </div>
            {showResolved &&
              resolvedIssues.map((i) => (
                <div
                  key={i.id}
                  className="pe-resolved-row"
                  onClick={() => setSelectedId(i.id)}
                >
                  <span className="pe-resolved-label">{caseLabel(i)}</span>
                  <span className="pe-resolved-tag">risolto</span>
                </div>
              ))}
          </>
        )}
      </div>

      {view === "lista" && (
        <div className="pe-batch-bar">
          <Button type="primary" ghost block onClick={batchHighConfidence}>
            Correggi tutti quelli sicuri (≥90%)
          </Button>
        </div>
      )}
    </div>
  );

  // ---- SEGMENT + FIX (center) ----
  const rowSourceOf = (rn: number) => rows.find((r) => r.row_number === rn)?.source || "";
  const fixPane = !selected ? (
    <div className="pe-fix pe-empty">Seleziona un problema per vedere la correzione.</div>
  ) : (() => {
    const changedOcc = (selected.occurrences || []).filter((o) => !o.ok);
    const isTerm = changedOcc.length > 0;
    return (
      <div className="pe-fix">
        <div className="pe-panel-header pe-fix-head">
          <EditOutlined className="pe-panel-icon" />
          <span className="pe-panel-title">Correzione</span>
          <span className="pe-fix-meta">
            <span className="badge badge--neutral">{CLASS_META[selected.class].label}</span>
            <span className={`badge badge--${selected.severity}`}>{selected.severity}</span>
            <span className="meta">{Math.round(selected.confidence * 100)}% affidabilità</span>
          </span>
        </div>

        {isTerm ? (
          <>
            <div className="pe-term-headline">
              Termine: «{selected.term || "?"}» → «{selected.canonical || "?"}»</div>
            {selected.renderings && selected.renderings.length > 0 && (
              <div className="pe-variants">
                Ora è tradotto in {selected.renderings.length} modi:{" "}
                {selected.renderings.map((r) => (
                  <Tag key={r} color={r === selected.canonical ? "green" : "default"}>
                    {r}
                  </Tag>
                ))}
                l'AI uniforma a «<b>{selected.canonical}</b>».
              </div>
            )}
            <div className="pe-help">
              {changedOcc.length}{" "}
              {changedOcc.length === 1 ? "riga da correggere" : "righe da correggere"}, ognuna con
              la concordanza giusta (genere/numero/articoli). Correggi tutte, oppure rivedi e
              conferma riga per riga.
            </div>
            <div className="pe-actions">
              <Button type="primary" onClick={() => acceptIssue(selected)}>
                Correggi tutte ({changedOcc.length})
              </Button>
              <Button danger onClick={() => rejectIssue(selected)}>
                Ignora
              </Button>
            </div>
            <div className="pe-occurrences">
              <div className="pe-field-label">Modifiche riga per riga</div>
              {changedOcc.map((o) => {
                const rn = o.row_number;
                const applied = occApplied.has(rn);
                const editing = occEditing.has(rn);
                const value = occDraft[rn] ?? o.target;
                return (
                  <div key={rn} className={`pe-review-row ${applied ? "applied" : ""}`}>
                    <div className="pe-review-head">
                      <span className="pe-occ-row">#{rn}</span>
                      {applied ? (
                        <Tag color="success">applicata</Tag>
                      ) : (
                        <span style={{ display: "flex", gap: 6 }}>
                          {!editing && (
                            <Button
                              size="small"
                              onClick={() =>
                                setOccEditing((s) => new Set(s).add(rn))
                              }
                            >
                              Modifica
                            </Button>
                          )}
                          <Button size="small" type="primary" onClick={() => applyOne(rn, value)}>
                            Conferma
                          </Button>
                        </span>
                      )}
                    </div>
                    <label className="pe-field-label pe-review-lbl">Frase originale (source)</label>
                    <div className="pe-review-src">{stripHtml(rowSourceOf(rn))}</div>
                    <label className="pe-field-label pe-review-lbl">Correzione (termine evidenziato)</label>
                    {editing && !applied ? (
                      <Input.TextArea
                        className="pe-suggestion pe-review-tgt"
                        autoSize={{ minRows: 1 }}
                        value={value}
                        onChange={(e) => setOccDraft((d) => ({ ...d, [rn]: e.target.value }))}
                      />
                    ) : (
                      <div
                        className="pe-review-tgt-view"
                        dangerouslySetInnerHTML={{
                          __html: highlightTerm(value, selected.canonical),
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <label className="pe-field-label">Frase originale (source)</label>
            <div className="pe-src" dangerouslySetInnerHTML={{ __html: selected.source }} />

            <label className="pe-field-label">Traduzione attuale</label>
            <div className="pe-tgt" dangerouslySetInnerHTML={{ __html: selected.target }} />

            <label className="pe-field-label">Analisi AI</label>
            <div className="pe-why">{issueNote(selected)}</div>

            <label className="pe-field-label">Suggerimento (modificabile)</label>
            <Input.TextArea
              className="pe-suggestion"
              autoSize={{ minRows: 2 }}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />

            <div className="pe-actions">
              <Button type="primary" onClick={() => acceptIssue(selected, draft)}>
                Accetta
              </Button>
              <Button danger onClick={() => rejectIssue(selected)}>
                Rifiuta
              </Button>
            </div>
          </>
        )}
      </div>
    );
  })();

  // ---- PREVIEW (right) - with QE risk heatmap ----
  // penalty -> risk bucket: 0 clean, 1-2 light-PE, 3+ full-PE
  const riskLevel = (rn: number) => {
    const pen = segmentQe[String(rn)] || 0;
    if (pen >= 3) return "full";
    if (pen >= 1) return "light";
    return "";
  };
  const hasQe = Object.keys(segmentQe).length > 0;
  const fullCount = Object.values(segmentQe).filter((v) => v >= 3).length;
  const lightCount = Object.values(segmentQe).filter((v) => v >= 1 && v < 3).length;
  const preview = (
    <div className="pe-preview">
      <div className="pe-preview-fixed">
        <div className="pe-panel-header pe-preview-head">
          <EyeOutlined className="pe-panel-icon" />
          <span className="pe-panel-title">Anteprima documento</span>
          <span className="pe-header-spacer" />
          <Tooltip title="Nascondi anteprima">
            <button className="pe-icon-btn" onClick={collapsePreview} aria-label="Nascondi anteprima">
              <RightOutlined />
            </button>
          </Tooltip>
        </div>
        {hasQe && (
          <div className="pe-triage">
            <span className="pe-triage-item">
              <i className="pe-dot full" /> {fullCount} da rivedere
            </span>
            <span className="pe-triage-item">
              <i className="pe-dot light" /> {lightCount} da controllare
            </span>
          </div>
        )}
      </div>
      <div className="pe-preview-split">
        {/* SOURCE (top) — read-only context */}
        <div className="pe-preview-half">
          <div className="pe-half-label">Source</div>
          <div className="pe-half-scroll">
            {rowsByParagraph.map(([p, prs]) => (
              <div key={p} className="pe-para">
                {prs.map((r) => {
                  const isCur =
                    selected?.row_number === r.row_number ||
                    selected?.occurrences?.some((o) => o.row_number === r.row_number && !o.ok);
                  return (
                    <span
                      key={r.row_number}
                      className={`pe-prow ${isCur ? "current" : ""}`}
                      dangerouslySetInnerHTML={{ __html: r.source || "" }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* TARGET (bottom) — heatmap + clickable */}
        <div className="pe-preview-half">
          <div className="pe-half-label">Traduzione</div>
          <div className="pe-half-scroll" ref={previewScrollRef}>
            {rowsByParagraph.map(([p, prs]) => (
              <div key={p} className="pe-para">
                {prs.map((r) => {
                  const isCurrent =
                    selected?.row_number === r.row_number ||
                    selected?.occurrences?.some((o) => o.row_number === r.row_number && !o.ok);
                  const risk = riskLevel(r.row_number);
                  const rowIssue = issues.find(
                    (i) =>
                      !done.has(i.id) &&
                      (i.row_number === r.row_number ||
                        i.occurrences?.some((o) => o.row_number === r.row_number && !o.ok))
                  );
                  return (
                    <span
                      key={r.row_number}
                      ref={isCurrent ? previewRowRef : undefined}
                      className={`pe-prow risk-${risk || "none"} ${isCurrent ? "current" : ""} ${
                        rowIssue ? "has-issue" : ""
                      }`}
                      onClick={rowIssue ? () => setSelectedId(rowIssue.id) : undefined}
                      dangerouslySetInnerHTML={{ __html: r.target || "" }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // ---- FOCUS view ----
  const focusView = selected ? (
    <div className="pe-focus-wrap">
      <div className="pe-focus-main">
        <div className="pe-focus-card">
          <div className="pe-panel-header pe-fix-head">
            <span className="dot" style={{ background: CLASS_META[selected.class].color }} />
            <span className="pe-panel-title">{CLASS_META[selected.class].label}</span>
            <span className="pe-fix-meta">
              <span className={`badge badge--${selected.severity}`}>{selected.severity}</span>
              <span className="meta">{Math.round(selected.confidence * 100)}% affidabilità</span>
            </span>
          </div>

          <label className="pe-field-label">SRC</label>
          <div className="pe-src" dangerouslySetInnerHTML={{ __html: selected.source }} />

          <label className="pe-field-label">TGT (attuale)</label>
          <div className="pe-tgt" dangerouslySetInnerHTML={{ __html: selected.target }} />

          <label className="pe-field-label">Suggerimento (modificabile)</label>
          <Input.TextArea
            id="pe-focus-edit"
            className="pe-suggestion"
            autoSize={{ minRows: 2 }}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="pe-rationale">{selected.rationale}</div>

          <div className="pe-actions">
            <Button type="primary" onClick={() => acceptIssue(selected, draft)}>
              Accetta <kbd>a</kbd>
            </Button>
            <Button danger onClick={() => rejectIssue(selected)}>
              Salta <kbd>x</kbd>
            </Button>
            <Button onClick={goNext}>
              Prossima <kbd></kbd>
            </Button>
          </div>
        </div>
      </div>

      <div className="pe-focus-strip">
        <div className="pe-field-label">Contesto</div>
        <div className="pe-focus-before">
          <span className="pe-strike">{stripHtml(selected.target)}</span>
        </div>
        <div className="pe-focus-after">{draft}</div>
      </div>
    </div>
  ) : (
    <div className="pe-empty" style={{ padding: 40 }}>
      Nessuna issue aperta.
    </div>
  );

  // ---- top-level render ----
  if (status === "loading") {
    return (
      <div className="pe-loading">
        <Spin size="large" />
        <p>L'AI sta analizzando la traduzione…</p>
        <p className="pe-loading-sub">
          La prima analisi può richiedere qualche minuto. Il risultato viene salvato:
          le prossime aperture sono istantanee.
        </p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="pe-cockpit">
        {header}
        <Alert
          type="error"
          showIcon
          message="Errore durante l'analisi della traduzione."
          style={{ margin: 24 }}
        />
      </div>
    );
  }

  return (
    <div className="pe-cockpit">
      {header}
      {view === "focus" ? (
        <>
          <div className="pe-focus-layout">
            <div className="pe-focus-inbox">{inbox}</div>
            {focusView}
          </div>
          <Progress
            percent={total ? Math.round((resolvedCount / total) * 100) : 0}
            style={{ padding: "0 24px" }}
          />
        </>
      ) : (
        <div className="pe-lista-flex" ref={listaRef}>
          {inboxOpen ? (
            <>
              <div className="pe-panel-left" style={{ width: inboxW }}>{inbox}</div>
              <div className="pe-resize" onMouseDown={startResize("inbox")} />
            </>
          ) : (
            <Tooltip title="Mostra problemi" placement="right">
              <button
                className="pe-preview-rail pe-rail-left"
                onClick={() => setInboxOpen(true)}
                aria-label="Mostra problemi"
              >
                <RightOutlined />
              </button>
            </Tooltip>
          )}

          <div className="pe-panel-center">{fixPane}</div>

          {showPreview ? (
            <>
              <div className="pe-resize" onMouseDown={startResize("preview")} />
              <div className="pe-panel-right" style={{ width: previewW }}>{preview}</div>
            </>
          ) : (
            <Tooltip title="Mostra anteprima" placement="left">
              <button
                className="pe-preview-rail"
                onClick={() => setShowPreview(true)}
                aria-label="Mostra anteprima"
              >
                <LeftOutlined />
              </button>
            </Tooltip>
          )}
        </div>
      )}

      {showHowto && openCount > 0 && (
        <div className="pe-howto-banner">
          <span className="pe-howto-text">
            L'AI ha trovato <b>{openCount}</b> {openCount === 1 ? "problema" : "problemi"}.
            Seleziona un problema a sinistra, controlla la correzione (in verde) e{" "}
            <b>accetta</b>, <b>modifica</b> o <b>rifiuta</b>.
          </span>
          <button
            className="pe-howto-close"
            onClick={() => setShowHowto(false)}
            aria-label="Chiudi"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

export default PECockpit;
